import React, { useState, useEffect } from 'react';
import { CheckIcon, ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { BuildingOfficeIcon, DocumentArrowUpIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/solid';
import { CompanyInfoStep } from './CompanyInfoStep';
import { DocumentsStep } from './DocumentsStep';
import { SummaryStep } from './SummaryStep';
import { Header } from '../common/Header';
import { useAuth } from '../../contexts/AuthContext';
import { Company } from '../../types/auth';

export interface Document {
    id: number;
    type: string;
    typeDescription: string;
    fileName: string;
    fileSize: number;
    contentType: string;
    status: string;
    uploadedAt: string;
    rejectionReason?: string;
    fileUrl?: string;
}

const steps = [
    {
        id: 1,
        name: 'Информация о компании',
        description: 'БИН и данные компании',
        icon: BuildingOfficeIcon,
    },
    {
        id: 2,
        name: 'Документы',
        description: 'Загрузка необходимых документов',
        icon: DocumentArrowUpIcon,
    },
    {
        id: 3,
        name: 'Итоги',
        description: 'Проверка и отправка на модерацию',
        icon: ClipboardDocumentCheckIcon,
    },
];

export const RegistrationWizard: React.FC = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [company, setCompany] = useState<Company | null>(null);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isStep1Valid, setIsStep1Valid] = useState(false);
    const { user } = useAuth();

    // Определяем, какой шаг должен быть активным на основе статуса пользователя
    useEffect(() => {
        if (user?.status === 'PENDING_MODERATION') {
            // Если заявка уже на модерации, показываем итоги
            setCurrentStep(3);
        } else if (user?.status === 'NEEDS_REVISION') {
            // Если нужна доработка, можем редактировать
            setCurrentStep(1);
        }
    }, [user?.status]);

    const isStepCompleted = (step: number) => {
        switch (step) {
            case 1:
                // Проверяем, что компания есть И все обязательные поля заполнены
                return company !== null && isStep1Valid;
            case 2:
                return documents.length > 0 && documents.some(d => d.status !== 'REJECTED');
            case 3:
                return user?.status === 'PENDING_MODERATION' || user?.status === 'APPROVED';
            default:
                return false;
        }
    };

    const isStepAccessible = (step: number) => {
        if (user?.status === 'PENDING_MODERATION' || user?.status === 'APPROVED') {
            return true; // Все шаги доступны для просмотра
        }

        switch (step) {
            case 1:
                return true;
            case 2:
                return isStepCompleted(1);
            case 3:
                return isStepCompleted(1) && isStepCompleted(2);
            default:
                return false;
        }
    };

    const canProceedToNext = () => {
        return isStepCompleted(currentStep);
    };

    const handleNext = () => {
        if (canProceedToNext() && currentStep < 3) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const canGoBack = () => {
        return currentStep > 1;
    };

    const handleStepClick = (step: number) => {
        if (isStepAccessible(step)) {
            setCurrentStep(step);
        }
    };

    const handleCompanyUpdate = React.useCallback((updatedCompany: Company) => {
        setCompany(updatedCompany);
    }, []);

    const handleDocumentsUpdate = React.useCallback((updatedDocuments: Document[]) => {
        setDocuments(updatedDocuments);
    }, []);

    const handleStep1ValidityChange = React.useCallback((isValid: boolean) => {
        setIsStep1Valid(isValid);
    }, []);

    const renderStepContent = () => {
        const isReadOnly = user?.status === 'PENDING_MODERATION' || user?.status === 'APPROVED';

        switch (currentStep) {
            case 1:
                return (
                    <CompanyInfoStep
                        company={company}
                        onCompanyUpdate={handleCompanyUpdate}
                        isReadOnly={isReadOnly}
                        onValidityChange={handleStep1ValidityChange}
                    />
                );
            case 2:
                return (
                    <DocumentsStep
                        documents={documents}
                        onDocumentsUpdate={handleDocumentsUpdate}
                        isReadOnly={isReadOnly}
                    />
                );
            case 3:
                return (
                    <SummaryStep
                        company={company}
                        documents={documents}
                        onSubmit={() => setIsSubmitting(true)}
                        isSubmitting={isSubmitting}
                    />
                );
            default:
                return null;
        }
    };

    const getStepStatus = (step: number) => {
        if (step < currentStep || isStepCompleted(step)) {
            return 'completed';
        } else if (step === currentStep) {
            return 'current';
        } else if (isStepAccessible(step)) {
            return 'upcoming';
        } else {
            return 'disabled';
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Header
                showBackButton={true}
                backButtonText="На главную"
                backButtonPath="/dashboard"
                title="Регистрация компании"
            />

            <div className="max-w-6xl mx-auto p-6">
                {/* Progress Steps */}
                <div className="mb-8">
                    <nav aria-label="Progress">
                        <div className="flex items-center justify-center">
                            {steps.map((step, stepIdx) => {
                                const status = getStepStatus(step.id);
                                const isLast = stepIdx === steps.length - 1;

                                return (
                                    <div key={step.name} className="flex items-center">
                                        {/* Step Circle and Content */}
                                        <div className="flex flex-col items-center">
                                            {status === 'completed' ? (
                                                <div
                                                    onClick={() => handleStepClick(step.id)}
                                                    className="cursor-pointer group flex flex-col items-center"
                                                >
                                                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-600 group-hover:bg-green-700 shadow-lg transition-all duration-200">
                                                        <CheckIcon className="h-6 w-6 text-white" aria-hidden="true" />
                                                    </div>
                                                    <div className="mt-3 text-center">
                                                        <p className="text-sm font-semibold text-green-600 group-hover:text-green-700">
                                                            {step.name}
                                                        </p>
                                                        <p className="text-xs text-green-500 mt-1">
                                                            Завершено
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : status === 'current' ? (
                                                <div className="flex flex-col items-center" aria-current="step">
                                                    <div className="flex h-12 w-12 items-center justify-center rounded-full border-3 border-blue-600 bg-white shadow-lg ring-4 ring-blue-100">
                                                        <step.icon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                                                    </div>
                                                    <div className="mt-3 text-center">
                                                        <p className="text-sm font-semibold text-blue-600">
                                                            {step.name}
                                                        </p>
                                                        <p className="text-xs text-blue-500 mt-1">
                                                            Текущий шаг
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : status === 'upcoming' ? (
                                                <div
                                                    onClick={() => handleStepClick(step.id)}
                                                    className="cursor-pointer group flex flex-col items-center"
                                                >
                                                    <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-gray-300 bg-white group-hover:border-gray-400 group-hover:shadow-md transition-all duration-200">
                                                        <step.icon className="h-6 w-6 text-gray-400 group-hover:text-gray-600" aria-hidden="true" />
                                                    </div>
                                                    <div className="mt-3 text-center">
                                                        <p className="text-sm font-medium text-gray-500 group-hover:text-gray-700">
                                                            {step.name}
                                                        </p>
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            Ожидает
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col items-center">
                                                    <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-gray-200 bg-gray-50">
                                                        <step.icon className="h-6 w-6 text-gray-300" aria-hidden="true" />
                                                    </div>
                                                    <div className="mt-3 text-center">
                                                        <p className="text-sm font-medium text-gray-300">
                                                            {step.name}
                                                        </p>
                                                        <p className="text-xs text-gray-300 mt-1">
                                                            Заблокировано
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Connector Line */}
                                        {!isLast && (
                                            <div className="flex-1 mx-4 mt-6">
                                                <div className="relative h-1 bg-gray-200 rounded-full overflow-hidden">
                                                    <div
                                                        className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ${status === 'completed'
                                                            ? 'bg-green-600 w-full'
                                                            : status === 'current'
                                                                ? 'bg-gradient-to-r from-green-600 to-blue-600 w-1/2'
                                                                : 'bg-gray-200 w-0'
                                                            }`}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </nav>
                </div>

                {/* Step Content */}
                <div className="bg-white rounded-lg shadow border border-gray-200">
                    <div className="px-6 py-8">
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {steps[currentStep - 1].name}
                            </h2>
                            <p className="text-gray-600 mt-1">
                                {steps[currentStep - 1].description}
                            </p>
                        </div>

                        {renderStepContent()}
                    </div>

                    {/* Navigation */}
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
                        <button
                            onClick={handlePrevious}
                            disabled={!canGoBack()}
                            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                            <ChevronLeftIcon className="h-4 w-4 mr-2" />
                            Назад
                        </button>

                        {currentStep < 3 ? (
                            <button
                                onClick={handleNext}
                                disabled={!canProceedToNext()}
                                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                Далее
                                <ChevronRightIcon className="h-4 w-4 ml-2" />
                            </button>
                        ) : (
                            <div className="text-sm text-gray-500">
                                Последний шаг
                            </div>
                        )}
                    </div>
                </div>

                {/* Status Information */}
                {user?.status && user.status !== 'DRAFT' && (
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                {user.status === 'PENDING_MODERATION' && (
                                    <div className="h-3 w-3 bg-yellow-400 rounded-full animate-pulse"></div>
                                )}
                                {user.status === 'APPROVED' && (
                                    <CheckIcon className="h-5 w-5 text-green-500" />
                                )}
                                {user.status === 'NEEDS_REVISION' && (
                                    <div className="h-3 w-3 bg-orange-400 rounded-full"></div>
                                )}
                                {user.status === 'REJECTED' && (
                                    <div className="h-3 w-3 bg-red-400 rounded-full"></div>
                                )}
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-blue-800">
                                    {user.status === 'PENDING_MODERATION' && 'Заявка на модерации'}
                                    {user.status === 'APPROVED' && 'Заявка одобрена'}
                                    {user.status === 'NEEDS_REVISION' && 'Требуется доработка'}
                                    {user.status === 'REJECTED' && 'Заявка отклонена'}
                                </h3>
                                <div className="mt-2 text-sm text-blue-700">
                                    {user.status === 'PENDING_MODERATION' && (
                                        <p>Ваша заявка рассматривается модераторами. Мы уведомим вас о результате.</p>
                                    )}
                                    {user.status === 'APPROVED' && (
                                        <p>Поздравляем! Ваша заявка одобрена. Теперь вы можете пользоваться всеми функциями системы.</p>
                                    )}
                                    {user.status === 'NEEDS_REVISION' && (
                                        <p>Модератор запросил доработку документов. Внесите необходимые изменения и подайте заявку повторно.</p>
                                    )}
                                    {user.status === 'REJECTED' && (
                                        <p>К сожалению, ваша заявка была отклонена. Обратитесь в поддержку для получения дополнительной информации.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
